import { ref } from 'vue'

// Toast composable — module-scoped state shared across every caller of
// useToast(), so any handler can fire a toast and one container at the
// app root renders the queue.
//
// Replaces the ~17 `setTimeout` flag patterns scattered across views
// (saved.value = true; setTimeout(() => saved.value = false, 2200)).
// Each surface used to roll its own toast — now they all share one
// stack with consistent placement, motion, and accessibility.

export type ToastKind = 'success' | 'error' | 'warning' | 'info'

export interface ToastAction {
  label: string
  onClick: () => void
}

export interface ToastOptions {
  // 0 disables auto-dismiss (sticky). Errors that need a user response
  // should set this. Defaults: success/info 3s, error 6s.
  duration?: number
  action?: ToastAction
}

export interface Toast {
  id: number
  kind: ToastKind
  message: string
  action?: ToastAction
}

interface InternalToast extends Toast {
  duration: number
  // Auto-dismiss timer handle. null while paused (hover) or sticky.
  timer: ReturnType<typeof setTimeout> | null
  // Time remaining in the current scheduled dismissal — used to resume
  // after pause without restarting the clock from full duration.
  remaining: number
  startedAt: number
}

const toasts = ref<InternalToast[]>([])
let nextId = 1

const DEFAULT_DURATION: Record<ToastKind, number> = {
  success: 3000,
  info: 3000,
  // Warnings need a beat longer than success but aren't blocking.
  warning: 4500,
  // Errors stay longer so the user has time to read what went wrong.
  error: 6000,
}

function show(kind: ToastKind, message: string, opts: ToastOptions = {}): number {
  const id = nextId++
  const duration = opts.duration ?? DEFAULT_DURATION[kind]
  const toast: InternalToast = {
    id,
    kind,
    message,
    action: opts.action,
    duration,
    timer: null,
    remaining: duration,
    startedAt: Date.now(),
  }
  if (duration > 0) {
    toast.timer = setTimeout(() => dismiss(id), duration)
  }
  toasts.value.push(toast)
  return id
}

function dismiss(id: number): void {
  const i = toasts.value.findIndex((t) => t.id === id)
  if (i === -1) return
  const toast = toasts.value[i]
  if (toast.timer) clearTimeout(toast.timer)
  toasts.value.splice(i, 1)
}

function pause(id: number): void {
  const toast = toasts.value.find((t) => t.id === id)
  if (!toast?.timer || toast.duration === 0) return
  clearTimeout(toast.timer)
  toast.timer = null
  toast.remaining = toast.remaining - (Date.now() - toast.startedAt)
}

function resume(id: number): void {
  const toast = toasts.value.find((t) => t.id === id)
  if (!toast || toast.timer || toast.duration === 0) return
  toast.startedAt = Date.now()
  toast.timer = setTimeout(() => dismiss(toast.id), Math.max(toast.remaining, 0))
}

export function useToast() {
  return {
    success: (message: string, opts?: ToastOptions) => show('success', message, opts),
    error: (message: string, opts?: ToastOptions) => show('error', message, opts),
    warning: (message: string, opts?: ToastOptions) => show('warning', message, opts),
    info: (message: string, opts?: ToastOptions) => show('info', message, opts),
    dismiss,
  }
}

// Internal accessor — only StemneToastContainer should reach into the
// queue + pause/resume hooks. Application code uses useToast() above.
export function useToastsInternal() {
  return { toasts, pause, resume, dismiss }
}
